/**
 * Sending a till's unsent bills before it erases itself.
 *
 * A till told to wipe, or decommissioned, may hold bills it made offline
 * that the server has never seen - tax invoices whose only copy is here.
 * Before erasing, they go up on the device's own credential
 * (POST /api/device/final-sync), which still works for a decommissioned
 * device and does not need anyone to be signed in (an account that is
 * closing has no one who can).
 *
 * If anything is still unsent afterwards, the caller does not erase: it
 * tries again on the next heartbeat. Bills the server refused (dated after
 * the decommission, totals that do not reconcile) cannot be delivered by
 * any retry, so they do not hold the erase back; they are counted in what
 * the device reports.
 */
import { LocalDataError, SyncEngine, SyncTransportError, type LocalDatabase, type SyncTransport } from '@kortex/pos-local';
import type { SyncPullQuery, SyncPullResponse, SyncPushRequest, SyncPushResponse } from '@kortex/shared';
import { DeviceApiError, sendFinalSync, type DeviceCredentials } from './deviceApi';
import { getActiveLocalDatabase, resumeLocalDatabaseForUnlock } from './posData';

class FinalSyncTransport implements SyncTransport {
  constructor(private readonly credentials: DeviceCredentials) {}

  async push(request: SyncPushRequest): Promise<SyncPushResponse> {
    try {
      return await sendFinalSync(this.credentials, request);
    } catch (error) {
      if (error instanceof DeviceApiError) {
        throw new SyncTransportError(error.status === 401 || error.status === 403 ? 'AUTH' : 'TRANSIENT', error.message);
      }
      throw new SyncTransportError('TRANSIENT', error instanceof Error ? error.message : String(error));
    }
  }

  pull(_query: SyncPullQuery): Promise<SyncPullResponse> {
    return Promise.reject(new SyncTransportError('PERMANENT', 'A final sync never pulls'));
  }
}

export interface FinalSyncOutcome {
  /** Still only on this device: do not erase. */
  unsent: number;
  /** Refused by the server; no retry can deliver these. */
  rejected: number;
}

/** Sends what this device has not sent yet. Throws if its database cannot be read. */
export async function sendUnsentWork(credentials: DeviceCredentials): Promise<FinalSyncOutcome> {
  let db: LocalDatabase | null = getActiveLocalDatabase();
  let opened = false;
  if (!db) {
    try {
      db = await resumeLocalDatabaseForUnlock();
      opened = true;
    } catch (error) {
      // Never set up for offline use: nothing can be unsent.
      if (error instanceof LocalDataError && error.code === 'NOT_PROVISIONED') return { unsent: 0, rejected: 0 };
      throw error;
    }
  }
  try {
    return await new SyncEngine(db, new FinalSyncTransport(credentials), { appVersion: 'web' }).flushPush();
  } finally {
    if (opened) await db.close().catch(() => undefined);
  }
}
